import React from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Lock, AlertTriangle } from 'lucide-react';
import { Badge } from '../ui/badge';

/**
 * MID-04-B (FE-01/FE-02/FE-05) — canonical WhatsApp awareness for the
 * EXISTING provider conversation view. No new messaging page, no new
 * endpoint, no new authority:
 *   • channel identity comes from the thread the inbox already loaded
 *     (`active_channels`, `external_thread_ids.whatsapp`);
 *   • eligibility + lock reason come from the existing canonical
 *     `GET /api/v2/threads/{id}/communication-policy` (same endpoint the
 *     patient UI uses, read through `useCommunicationPolicy`);
 *   • the originating connected Provider number comes from the existing
 *     provider Meta connection projection.
 */

export const isWhatsAppThread = (thread) => {
  if (!thread) return false;
  const channels = thread.active_channels || [];
  return channels.includes('whatsapp')
    || !!(thread.external_thread_ids && thread.external_thread_ids.whatsapp);
};

export const whatsAppAnchorPhoneNumberId = (thread) =>
  ((thread || {}).external_thread_ids || {}).whatsapp?.provider_account_id || null;

export const WhatsAppThreadBadge = ({ thread }) => {
  const { t } = useTranslation();
  if (!isWhatsAppThread(thread)) return null;
  return (
    <Badge
      className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] gap-1"
      data-testid="thread-channel-whatsapp-badge"
    >
      <Phone className="h-3 w-3" />
      {t('provider.messaging.whatsapp.channelBadge')}
    </Badge>
  );
};

export const WhatsAppChannelStrip = ({ thread, policy, senderNumber, canSend = true }) => {
  const { t } = useTranslation();
  if (!isWhatsAppThread(thread)) return null;

  const eligible = policy ? policy.whatsapp_eligible === true : null;
  const reasonKey = policy?.reason ? `provider.messaging.whatsapp.reason.${policy.reason}` : null;
  const reasonText = reasonKey && t(reasonKey) !== reasonKey
    ? t(reasonKey)
    : t('provider.messaging.whatsapp.reason.generic');

  return (
    <div
      className={`px-4 py-2 border-b text-xs flex flex-wrap items-center gap-x-2 gap-y-1 ${
        eligible === false ? 'bg-amber-50 border-amber-200 text-amber-800'
          : 'bg-emerald-50 border-emerald-200 text-emerald-800'
      }`}
      data-testid="whatsapp-channel-strip"
    >
      {eligible === false ? <Lock className="h-3.5 w-3.5 shrink-0" />
        : <Phone className="h-3.5 w-3.5 shrink-0" />}

      {eligible === false ? (
        <span data-testid="whatsapp-send-locked-reason">
          {t('provider.messaging.whatsapp.lockedPrefix')} {reasonText}
        </span>
      ) : (
        <span data-testid="whatsapp-send-eligible">
          {senderNumber
            ? t('provider.messaging.whatsapp.repliesLeaveFromNumber', { number: senderNumber })
            : t('provider.messaging.whatsapp.repliesLeaveFromSameNumber')}
        </span>
      )}

      {!canSend && (
        <span className="text-slate-600" data-testid="whatsapp-no-send-permission">
          {t('provider.messaging.whatsapp.noSendPermission')}
        </span>
      )}

      <span className="inline-flex items-center gap-1 text-slate-500" data-testid="whatsapp-media-unsupported">
        <AlertTriangle className="h-3 w-3" />
        {t('provider.messaging.whatsapp.mediaUnsupported')}
      </span>
    </div>
  );
};

export default WhatsAppChannelStrip;
